import appManager from '@app/appManager';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import FeatureFlagModule from '@modules/FeatureFlagModule';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule/featureFlagKeys';

const TRACE_ID_HEADER = 'X-Trace-Id';
const REQUEST_DURATION_HEADER = 'X-Request-Duration-Ms';

/**
 * Performs a fetch request and logs the request and response details.
 *
 * @param input - The resource that you wish to fetch. This can either be a
 * string containing the URL of the resource, or a `Request` object.
 * @param init - An optional object containing any custom settings that you
 * want to apply to the request.
 * @returns A `Promise` that resolves to a `Response` object, with an
 * overridden `text` method that logs errors if present.
 *
 * The function logs the following details:
 * - The operation name from the request body.
 * - The frontend request duration (total time from FE perspective).
 * - The backend processing duration (from X-Request-Duration-Ms header).
 * - The network latency (FE duration - BE duration).
 * - The trace ID from the response headers.
 * - The variables from the request body.
 * - Any errors present in the response.
 * - Whether the browser tab was visible during the request.
 */
async function loggingFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let body: { operationName?: string; variables?: unknown } = {};
  let operationName = '';

  if (init?.body && typeof init.body === 'string') {
    body = JSON.parse(init.body);
    operationName = body.operationName ?? '';
  } else {
    operationName = 'FileUpload';
  }

  // Use performance.now() for monotonic timing (unaffected by NTP/clock changes)
  const startPerf = performance.now();
  // Use Date.now() only for wall-clock timestamps in logs and events
  const timestamp = new Date().toISOString();

  let response: Response;
  try {
    response = await fetch(input, init);
  } catch (error) {
    const duration = performance.now() - startPerf;

    if (error instanceof DOMException && error.name === 'AbortError') {
      // AbortError = page refresh or user cancel — not a network failure
      appManager
        .logger('api')
        .debug(`request for '${operationName}' was aborted`, { operationName });
      throw error;
    }

    // TypeError = network failure (DNS, connection refused, timeout)
    appManager.logger('api').error(`network failure for '${operationName}'`, {
      operationName,
      duration,
      error: error instanceof Error ? error.message : String(error),
    });

    appManager.events.emit('network.request.failed', {
      operationName,
      duration,
      timestamp,
      errorType: 'NetworkFailure',
    });

    throw error;
  }

  // Calculate latency metrics using performance.now() delta for accuracy
  const frontendRequestDuration = performance.now() - startPerf;
  const backendDurationMs = response.headers.get(REQUEST_DURATION_HEADER);
  const backendDuration = backendDurationMs
    ? parseInt(backendDurationMs, 10)
    : undefined;
  const networkLatency =
    backendDuration !== undefined
      ? frontendRequestDuration - backendDuration
      : undefined;

  const tabVisible = !document.hidden;

  appManager
    .logger('api')
    .info(`request for '${operationName}' in ${frontendRequestDuration}ms`, {
      traceId: response.headers.get(TRACE_ID_HEADER),
      variables: body.variables,
      frontendRequestDurationMs: frontendRequestDuration,
      backendProcessingDurationMs: backendDuration,
      networkLatencyMs: networkLatency,
      tabVisible,
    });

  if (!tabVisible) {
    appManager.logger('api').debug('Skipping latency - tab was hidden');
  }

  if (
    FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureNetworkMonitoringEnabled,
    ) &&
    tabVisible &&
    networkLatency !== undefined &&
    backendDuration !== undefined
  ) {
    DiagnosticsModule.reportNetworkLatency({
      networkLatencyMs: networkLatency,
      frontendRequestDurationMs: frontendRequestDuration,
      backendProcessingDurationMs: backendDuration,
      operationName,
      timestamp,
    });
  }

  // Store the original text method
  const originalText = response.text.bind(response);

  // Override the text method to add error logging
  response.text = async () => {
    const resultText = await originalText();

    try {
      const result = JSON.parse(resultText);

      if (result.errors) {
        appManager
          .logger('api')
          .error(`request for '${operationName}' failed`, {
            traceId: response.headers.get(TRACE_ID_HEADER),
            variables: body.variables,
            errors: result.errors,
          });
      }
    } catch (error) {
      appManager
        .logger('api')
        .error(`Failed to parse response for '${operationName}'`, {
          error: error instanceof Error ? error.message : String(error),
          traceId: response.headers.get(TRACE_ID_HEADER),
          variables: body.variables,
        });
    }

    return resultText;
  };

  return response;
}

export default loggingFetch;
